import React from "react";
import { Button as AntButton } from "antd";
import "./buttons.css";
import { BaseButtonProps } from "antd/es/button/button";
import useIsInIframe from "../../Hooks/useIsInIframe";


interface ButtonProps extends BaseButtonProps {
  ref?: React.Ref<HTMLButtonElement>;
  /**
   * Additional props to pass down to the html button element
   * @default {}
   */
  rest?: any;
  /**
   * Function to run when the button is clicked
   * @default undefined
   */
  onClick?: (e: any) => void;
  /**
   * Function to run when the button is pressed down
   * @default undefined
   */
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * The type of button to render
   * @default "button"
   */
  htmlType?: "button" | "submit" | "reset";
  /**
   * Whether to show a title when the button is hovered
   * @default false
   */
  hoverTitle?: boolean;
  id?: string;
  /**
   * Custom styles for the button
   */
  style?: React.CSSProperties;
  /**
   * CSS class name
   */
  className?: string;
}

const Button = ({ type = 'default', id, hoverTitle = false, htmlType="button", 'data-tour': dataTour, style, className, ...rest }: ButtonProps) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [showTitle, setShowTitle] = React.useState(false);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  // Detect if running in iframe
  const isInIframe = useIsInIframe();

  const handleMouseOver = () => {
    setIsHovered(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowTitle(true);
  };

  const handleMouseOut = () => {
    setIsHovered(false);
    timerRef.current = setTimeout(() => setShowTitle(false), 3000);
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Apply iframe color scheme for primary and bordered buttons
  const iframeStyle = isInIframe && (type === 'primary' || type === 'default') ? {
    backgroundColor: type === 'primary' ? '#0086C9' : undefined,
    borderColor: '#0086C9',
    color: type === 'default' ? '#0086C9' : undefined,
    ...style
  } : style;

  const iframeClassName = isInIframe && (type === 'primary' || type === 'default')
    ? `${className || ''} iframe-primary-button`.trim()
    : className;

  return (
    <AntButton
      htmlType={htmlType}
      type={type}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      data-tour={dataTour}
      style={iframeStyle}
      className={iframeClassName}
      {...rest}
      id={id}
    >
      {hoverTitle ? (isHovered || showTitle) ? rest.children : null : rest.children}
    </AntButton>
  )
};

export { Button };
